import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import type { Item } from "../../../api/menu";
import type { useOrderingMessage } from "../components/useOrderingMessage";

interface EndedLunchModalsOptions {
  allItems: Item[];
  /** Продуктът на „Инфо" / прозореца с добавките („Промени", „Поръчай пак") */
  selectedItemId: number | null;
  itemModalOpen: boolean;
  closeItemModals: () => void;
  /** Продуктът в прозореца „Модификатори/коментар" (моливът в прегледа) */
  editItemId: number | null;
  closeEditModal: () => void;
  message: ReturnType<typeof useOrderingMessage>;
}

/**
 * Обедното меню свърши, докато е отворен прозорец на негов продукт: копието
 * (отрицателно id) изчезва от каталога и прозорецът се скрива, но състоянието
 * „отворен" остава — следващият кликнат продукт го отваря сам (в режим
 * „Поръчай пак" — направо в сметката), а Enter/F9 не работят. Затваряме го
 * като с „Затвори" и казваме защо е изчезнал.
 */
export const useCloseEndedLunchModals = ({
  allItems,
  selectedItemId,
  itemModalOpen,
  closeItemModals,
  editItemId,
  closeEditModal,
  message,
}: EndedLunchModalsOptions) => {
  const { t } = useTranslation();

  useEffect(() => {
    // Празен каталог = зарежда се/грешка — не пипаме нищо
    if (allItems.length === 0) return;
    const vanished = (itemId: number | null) =>
      itemId != null && itemId < 0 && !allItems.some((item) => item.id === itemId);

    const itemModalVanished = itemModalOpen && vanished(selectedItemId);
    const editModalVanished = vanished(editItemId);
    if (itemModalVanished) closeItemModals();
    if (editModalVanished) closeEditModal();
    if (itemModalVanished || editModalVanished) message.warning(t("lunchMenu.modalClosedEnded"));
  }, [allItems, selectedItemId, itemModalOpen, closeItemModals, editItemId, closeEditModal, message, t]);
};
